// This optional code is used to register a service worker.
// register() is not called by default.

// This lets the app load faster on subsequent visits in production, and gives
// it offline capabilities. However, it also means that developers (and users)
// will only see deployed updates on subsequent visits to a page, after all the
// existing tabs open on the page have been closed, since previously cached
// resources are updated in the background.

// To learn more about the benefits of this model and instructions on how to
// opt-in, read https://bit.ly/CRA-PWA

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
    // [::1] is the IPv6 localhost address.
    window.location.hostname === '[::1]' ||
    // 127.0.0.0/8 are considered localhost for IPv4.
    window.location.hostname.match(
      /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/
    )
);

/**
 * Registers the service worker for the application if running in production and supported by the browser.
 * Handles different behaviors for localhost and production environments.
 *
 * @param {Object} [config] - Optional configuration object for service worker registration.
 * @param {function} [config.onSuccess] - Callback function to execute when the service worker is registered successfully.
 * @param {function} [config.onUpdate] - Callback function to execute when a new service worker is found and an update is available.
 */
export function register(config) {
  // Allow service worker in both development and production
  if ('serviceWorker' in navigator) {
    // The URL constructor is available in all browsers that support SW.
    const publicUrl = new URL(process.env.PUBLIC_URL || '', window.location.href);
    if (publicUrl.origin !== window.location.origin) {
      // Our service worker won't work if PUBLIC_URL is on a different origin
      // from what our page is served on. This might happen if a CDN is used to
      // serve assets; see https://github.com/facebook/create-react-app/issues/2374
      console.warn('⚠️ Service worker cannot register: PUBLIC_URL origin mismatch');
      return;
    }

    // Use standard service worker path for both dev and production
    const swUrl = `${process.env.PUBLIC_URL || ''}/service-worker.js`;

    // Register immediately if page already loaded, otherwise wait for load event
    const registerSW = () => {
      if (isLocalhost) {
        // This is running on localhost. Let's check if a service worker still exists or not.
        checkValidServiceWorker(swUrl, config);

        // Add some additional logging to localhost, pointing developers to the
        // service worker/PWA documentation.
        navigator.serviceWorker.ready.then(() => {
          console.log(
            'This web app is being served cache-first by a service ' +
              'worker. To learn more, visit https://bit.ly/CRA-PWA'
          );
        });
      } else {
        // Is not localhost. Just register service worker
        registerValidSW(swUrl, config);
      }
    };

    // Check if page already loaded
    if (document.readyState === 'complete') {
      console.log('📦 Page already loaded, registering SW immediately');
      registerSW();
    } else {
      window.addEventListener('load', registerSW);
    }
  } else {
    console.log('🚫 Service workers are not supported in this browser');
  }
}

/**
 * Registers a valid service worker and handles updates and caching events.
 *
 * @param {string} swUrl - The URL of the service worker script to register.
 * @param {Object} [config] - Optional configuration object.
 * @param {function} [config.onUpdate] - Callback executed when a new service worker is installed and waiting to activate.
 * @param {function} [config.onSuccess] - Callback executed when the service worker is successfully registered and content is cached for offline use.
 */
function registerValidSW(swUrl, config) {
  console.log('🔧 Attempting to register service worker:', swUrl);
  
  navigator.serviceWorker
    .register(swUrl)
    .then(registration => {
      console.log('✅ Service worker registered successfully:', registration);
      
      registration.onupdatefound = () => {
        const installingWorker = registration.installing;
        if (installingWorker == null) {
          return;
        }
        installingWorker.onstatechange = () => {
          if (installingWorker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              // At this point, the updated precached content has been fetched,
              // but the previous service worker will still serve the older
              // content until all client tabs are closed.
              console.log(
                'New content is available and will be used when all ' +
                  'tabs for this page are closed. See https://bit.ly/CRA-PWA.'
              );

              // Execute callback
              if (config && config.onUpdate) {
                config.onUpdate(registration);
              }
              // Send config to service worker on update
              if (navigator.serviceWorker.controller) {
                navigator.serviceWorker.controller.postMessage({ type: 'config', config });
              }
            } else {
              // At this point, everything has been precached.
              // It's the perfect time to display a
              // "Content is cached for offline use." message.
              console.log('Content is cached for offline use.');

              // Execute callback
              if (config && config.onSuccess) {
                config.onSuccess(registration);
              }
            }
          }
        };
      };
      // Send config to service worker on initial registration
      if (navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({ type: 'config', config });
      }
    })
    .catch(error => {
      console.error('❌ Error during service worker registration:', error);
      console.error('SW URL attempted:', swUrl);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
    });
}

/**
 * Checks if a valid service worker exists at the specified URL.
 * If the service worker is not found or is not a JavaScript file, it unregisters the current service worker and reloads the page.
 * If a valid service worker is found, it proceeds with registration.
 * Handles offline scenarios gracefully by logging a message.
 *
 * @param {string} swUrl - The URL of the service worker script to check.
 * @param {Object} config - Optional configuration object for service worker registration.
 */
function checkValidServiceWorker(swUrl, config) {
  // Check if the service worker can be found. If it can't reload the page.
  fetch(swUrl, {
    headers: { 'Service-Worker': 'script' },
  })
    .then(response => {
      // Ensure service worker exists, and that we really are getting a JS file.
      const contentType = response.headers.get('content-type');
      if (
        response.status === 404 ||
        (contentType != null && contentType.indexOf('javascript') === -1)
      ) {
        // No service worker found. Probably a different app. Reload the page.
        navigator.serviceWorker.ready.then(registration => {
          registration.unregister().then(() => {
            window.location.reload();
          });
        });
      } else {
        // Service worker found. Proceed as normal.
        registerValidSW(swUrl, config);
      }
    })
    .catch(() => {
      console.log(
        'No internet connection found. App is running in offline mode.'
      );
    });
}

/**
 * Unregisters the service worker if it is registered in the browser.
 * This function checks if the Service Worker API is available, waits for the service worker to be ready,
 * and then calls the unregister method to remove it. Errors during the process are logged to the console.
 */
export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then(registration => {
        registration.unregister();
      })
      .catch(error => {
        console.error(error.message);
      });
  }
}
